import "./ExpenseReportDetails.css";

import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";

import { useTranslation } from "../../src/i18n/UseTranslation";
import {
  ExpenseReport,
  StatusType,
} from "../models/expense_report";
import { ExpenseReportItem } from "../models/expense_report_item";
import { Attachment } from "../models/attachment";
import { apiCall } from "../api/apiCall";

// Import du composant de dialogue (ajuste le chemin si besoin)
import ConfirmDialog from "../components/Navbar/ConfirmLogoutDialog";

type ExpenseReportDetailsCardProps = {
  expenseReport: ExpenseReport;
  items: ExpenseReportItem[];
  attachments: Attachment[];
};

type AllowedAdminStatus = Extract<
  StatusType,
  "APPROVED" | "REJECTED" | "REIMBURSED"
>;

function ExpenseReportDetailsCard({
  expenseReport,
  items,
  attachments,
}: ExpenseReportDetailsCardProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const translations = t.expenseReportDetails;
  const translateCat = t.categoryTypes;

  const [previewUrls, setPreviewUrls] = useState<
    Record<string, string>
  >({});

  // États pour les boîtes de dialogue
  const [isDeleteExpenseDialogOpen, setIsDeleteExpenseDialogOpen] = useState(false);
  
  const [isDeleteItemDialogOpen, setIsDeleteItemDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);

  const [isUpdateStatusDialogOpen, setIsUpdateStatusDialogOpen] = useState(false);
  const [statusToUpdate, setStatusToUpdate] = useState<AllowedAdminStatus | null>(null);

  const statusTranslations: Partial<
    Record<StatusType, string>
  > = {
    DRAFT: translations.status.draft,
    PENDING: translations.status.pending,
    APPROVED: translations.status.approved,
    REJECTED: translations.status.rejected,
    REIMBURSED: translations.status.reimbursed,
  };

  const translateStatus = (status: StatusType): string => {
    return statusTranslations[status] ?? status;
  };

  const replaceVariable = (
    value: string,
    variable: string,
    replacement: string
  ): string => {
    return value.replace(`{${variable}}`, replacement);
  };

  const formatDate = (
    value: string | Date | null | undefined
  ): string => {
    if (!value) return "—";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return String(value);
    }

    return date.toLocaleDateString();
  };

  useEffect(() => {
    let isMounted = true;

    const fetchAttachmentUrls = async () => {
      const urls: Record<string, string> = {};

      await Promise.all(
        attachments.map(async (attachment) => {
          try {
            const data = await apiCall<{ url: string }>(
              `/attachments/${attachment.uuid}/download?action=view` 
            );

            urls[attachment.uuid] = data.url;
          } catch (error) {
            console.error(
              "Error fetching preview URL for",
              attachment.uuid,
              error
            );
          }
        })
      );

      if (isMounted) {
        setPreviewUrls(urls);
      }
    };

    if (attachments.length > 0) {
      fetchAttachmentUrls();
    } else {
      setPreviewUrls({});
    }

    return () => {
      isMounted = false;
    };
  }, [attachments]);

  // --- LOGIQUE DE SUPPRESSION DE LA NOTE DE FRAIS ENTIÈRE ---
  const handleDeleteExpenseClick = () => {
    setIsDeleteExpenseDialogOpen(true);
  };

  const executeDeleteExpense = async () => {
    setIsDeleteExpenseDialogOpen(false);
    try {
      await apiCall(`/expense-reports/${expenseReport.uuid}`, {
        method: "DELETE",
      });

      window.alert(translations.alerts.reportDeleted);
      navigate("/expense-reports");
    } catch (error) {
      console.error("Error deleting expense report:", error);
      window.alert(translations.alerts.reportDeleteError);
    }
  };

  const handleDeleteItemClick = (itemUuid: string) => {
    setItemToDelete(itemUuid);
    setIsDeleteItemDialogOpen(true);
  };

  const executeDeleteItem = async () => {
    if (!itemToDelete) return;
    setIsDeleteItemDialogOpen(false);
    try {
      await apiCall(`/items/${itemToDelete}`, {
        method: "DELETE",
      });

      window.alert(translations.alerts.itemDeleted);
      window.location.reload();
    } catch (error) {
      console.error("Error deleting expense item:", error);
      window.alert(translations.alerts.itemDeleteError);
    } finally {
      setItemToDelete(null);
    }
  };

  const handleUpdateStatusClick = (status: AllowedAdminStatus) => {
    setStatusToUpdate(status);
    setIsUpdateStatusDialogOpen(true);
  };

  const executeUpdateStatus = async () => {
    if (!statusToUpdate) return;
    setIsUpdateStatusDialogOpen(false);
    
    const translatedStatus = translateStatus(statusToUpdate);

    try {
      await apiCall(
        `/expense-reports/${expenseReport.uuid}/status`,
        {
          method: "PATCH",
          body: JSON.stringify({ status: statusToUpdate }),
        }
      );

      window.alert(
        replaceVariable(
          translations.alerts.statusUpdated,
          "status",
          translatedStatus
        )
      );

      window.location.reload();
    } catch (error) {
      console.error(
        "Error updating expense report status:",
        error
      );
      window.alert(translations.alerts.statusUpdateError);
    } finally {
      setStatusToUpdate(null);
    }
  };

  const handleViewAttachment = async (
    attachmentUuid: string
  ) => {
    try {
      const data = await apiCall<{ url: string }>(
        `/attachments/${attachmentUuid}/download?action=view`
      );

      window.open(
        data.url,
        "_blank",
        "noopener,noreferrer"
      );
    } catch (error) {
      console.error(
        "Error fetching attachment URL:",
        error
      );

      window.alert(
        translations.alerts.attachmentOpenError
      );
    }
  };

  const handleDownloadAttachment = async (
    attachmentUuid: string,
    fileName: string 
  ) => {
    try {
      const data = await apiCall<{ url: string }>(
        `/attachments/${attachmentUuid}/download?action=download`
      );

      const response = await fetch(data.url, {
        method: "GET",
        mode: "cors",
        cache: "no-cache", 
      });
      if (!response.ok) throw new Error("Erreur de téléchargement");
      
      const blob = await response.blob();
      const localUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = localUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      
      document.body.removeChild(link);
      window.URL.revokeObjectURL(localUrl);

    } catch (error) {
      console.error("Error downloading attachment:", error);
      window.alert(translations.alerts.attachmentOpenError);
    }
  };

  const renderStatusActions = () => {
    if (expenseReport.status === "PENDING") {
      return (
        <>
          <button
            type="button"
            className="expense-action-btn approve-btn"
            onClick={() =>
              handleUpdateStatusClick("APPROVED")
            }
          >
            {translations.actions.approve}
          </button>

          <button
            type="button"
            className="expense-action-btn reject-btn"
            onClick={() =>
              handleUpdateStatusClick("REJECTED")
            }
          >
            {translations.actions.reject}
          </button>
        </>
      );
    }

    if (expenseReport.status === "APPROVED") {
      return (
        <button
          type="button"
          className="expense-action-btn reimbursed-btn"
          onClick={() =>
            handleUpdateStatusClick("REIMBURSED")
          }
        >
          {translations.actions.markAsReimbursed}
        </button>
      );
    }

    return null;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) {
      return `${bytes} ${translations.fileSize.bytes}`;
    }

    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} ${
        translations.fileSize.kilobytes
      }`;
    }

    return `${(bytes / (1024 * 1024)).toFixed(1)} ${
      translations.fileSize.megabytes
    }`;
  };

  return (
    <div className="expense-report-layout-wrapper">
      <section className="expense-panel left-panel">
        <div className="expense-report-header">
          <div>
            <h1 className="expense-report-supplier-name">
              {expenseReport.supplier_name ||
                translations.title}
            </h1>

            <span
              className={`expense-status-badge status-${expenseReport.status.toLowerCase()}`}
            >
              {translateStatus(expenseReport.status)}
            </span>
          </div>

          <div className="header-actions">
            {renderStatusActions()}

            <button
              type="button"
              className="expense-action-btn category-delete-btn"
              onClick={handleDeleteExpenseClick}
            >
              {t.common.delete}
            </button>

            <button
              type="button"
              className="expense-action-btn edit-category-submit"
              onClick={() =>
                navigate(
                  `/expense-reports/${expenseReport.uuid}/edit`
                )
              }
            >
              {t.common.edit}
            </button>
          </div>
        </div>

        <hr className="expense-report-divider" />

        <div className="expense-report-grid">
          <div className="expense-report-field">
            <span className="expense-report-field-label">
              {translations.fields.amount}
            </span>

            <span className="expense-report-field-value">
              {Number(expenseReport.amount).toFixed(2)} €
            </span>
          </div>

          <div className="expense-report-field">
            <span className="expense-report-field-label">
              {translations.fields.receiptNumber}
            </span>

            <span className="expense-report-field-value">
              {expenseReport.receipt_number || "—"}
            </span>
          </div>

          <div className="expense-report-field">
            <span className="expense-report-field-label">
              {translations.fields.expenseDate}
            </span>

            <span className="expense-report-field-value">
              {formatDate(expenseReport.expense_date)}
            </span>
          </div>

          <div className="expense-report-field">
            <span className="expense-report-field-label">
              {translations.fields.vatAmount}
            </span>

            <span className="expense-report-field-value">
              {Number(
                expenseReport.vat_amount ?? 0
              ).toFixed(2)}{" "}
              €
            </span>
          </div>

          {expenseReport.submitted_at && (
            <div className="expense-report-field">
              <span className="expense-report-field-label">
                {translations.fields.submittedAt}
              </span>

              <span className="expense-report-field-value">
                {formatDate(expenseReport.submitted_at)}
              </span>
            </div>
          )}

          {expenseReport.comment && (
            <div className="expense-report-field expense-report-field-full">
              <span className="expense-report-field-label">
                {translations.fields.comment}
              </span>

              <span className="expense-report-field-value">
                {expenseReport.comment}
              </span>
            </div>
          )}

          {expenseReport.distance_km != null && (
            <div className="expense-report-field">
              <span className="expense-report-field-label">
                {translations.fields.distance}
              </span>

              <span className="expense-report-field-value">
                {Number(expenseReport.distance_km)} km
              </span>
            </div>
          )}

          {expenseReport.vehicle_power_cv != null && (
            <div className="expense-report-field">
              <span className="expense-report-field-label">
                {translations.fields.vehiclePower}
              </span>

              <span className="expense-report-field-value">
                {expenseReport.vehicle_power_cv} CV
              </span>
            </div>
          )}

          {expenseReport.vehicle_type && (
            <div className="expense-report-field">
              <span className="expense-report-field-label">
                {translations.fields.vehicleType}
              </span>

              <span className="expense-report-field-value">
                {expenseReport.vehicle_type}
              </span>
            </div>
          )}

          {expenseReport.energy_type && (
            <div className="expense-report-field">
              <span className="expense-report-field-label">
                {translations.fields.energyType}
              </span>

              <span className="expense-report-field-value">
                {expenseReport.energy_type}
              </span>
            </div>
          )}

          {expenseReport.mileage_rate_year != null && (
            <div className="expense-report-field">
              <span className="expense-report-field-label">
                {translations.fields.mileageRateYear}
              </span>

              <span className="expense-report-field-value">
                {expenseReport.mileage_rate_year}
              </span>
            </div>
          )}

          {expenseReport.calculated_reimbursement != null && (
            <div className="expense-report-field">
              <span className="expense-report-field-label">
                {
                  translations.fields
                    .calculatedReimbursement
                }
              </span>

              <span className="expense-report-field-value">
                {Number(
                  expenseReport.calculated_reimbursement
                ).toFixed(2)}{" "}
                €
              </span>
            </div>
          )}

          {expenseReport.participant_count != null && (
            <div className="expense-report-field">
              <span className="expense-report-field-label">
                {translations.fields.participants}
              </span>

              <span className="expense-report-field-value">
                {expenseReport.participant_count}
              </span>
            </div>
          )}

          {expenseReport.company && (
            <div className="expense-report-field">
              <span className="expense-report-field-label">
                {translations.fields.company}
              </span>

              <span className="expense-report-field-value">
                {expenseReport.company.name}
              </span>
            </div>
          )}

          {expenseReport.user && (
            <div className="expense-report-field">
              <span className="expense-report-field-label">
                {translations.fields.user}
              </span>

              <span className="expense-report-field-value">
                {expenseReport.user.first_name}{" "}
                {expenseReport.user.last_name}
              </span>
            </div>
          )}

          {expenseReport.category && (
            <div className="expense-report-field">
              <span className="expense-report-field-label">
                {translations.fields.category}
              </span>

              <span className="expense-report-field-value">
                {translateCat[expenseReport.category.item_type.toLowerCase() as keyof typeof translateCat] || expenseReport.category.item_type}
              </span>
            </div>
          )}
        </div>

        <hr className="expense-report-divider" />

        <div className="expense-report-items-header">
          <h2 className="expense-report-section-title">
            {translations.sections.items}
          </h2>
        </div>

        {items.length === 0 ? (
          <div className="expense-report-empty">
            {translations.empty.items}
          </div>
        ) : (
          <div className="expense-report-items-list">
            {items.map((item) => (
              <article
                key={item.uuid}
                className="expense-report-item-card"
              >
                <div className="expense-report-header">
                  <div>
                    <h3 className="expense-report-item-title">
                      {item.label}
                    </h3>

                    <span className="expense-item-type-badge">
                      {translateCat[item.item_type.toLowerCase() as keyof typeof translateCat] || item.item_type}
                    </span>
                  </div>

                  <div className="header-actions">
                    <button
                      type="button"
                      className="expense-action-btn category-delete-btn"
                      onClick={() => handleDeleteItemClick(item.uuid)}
                    >
                      {t.common.delete}
                    </button>

                    <button
                      type="button"
                      className="expense-action-btn edit-category-submit"
                      onClick={() => navigate(
                        `/expense-report-items/${expenseReport.uuid}/edit`
                      )}
                    >
                      {t.common.edit}
                    </button>
                  </div>
                </div>

                <hr className="expense-report-divider" />

                <div className="expense-report-item-grid">
                  <div className="expense-report-field">
                    <span className="expense-report-field-label">
                      {translations.itemFields.type}
                    </span>

                    <span className="expense-report-field-value">
                      {translateCat[item.item_type.toLowerCase() as keyof typeof translateCat] || item.item_type}
                    </span>
                  </div>

                  <div className="expense-report-field">
                    <span className="expense-report-field-label">
                      {translations.itemFields.quantity}
                    </span>

                    <span className="expense-report-field-value">
                      {Number(item.quantity)}
                    </span>
                  </div>

                  {item.unit_amount_ttc != null && (
                    <div className="expense-report-field">
                      <span className="expense-report-field-label">
                        {translations.itemFields
                          .unitAmountTtc}
                      </span>

                      <span className="expense-report-field-value">
                        {Number(
                          item.unit_amount_ttc
                        ).toFixed(2)}{" "}
                        €
                      </span>
                    </div>
                  )}

                  <div className="expense-report-field">
                    <span className="expense-report-field-label">
                      {translations.itemFields
                        .totalAmountTtc}
                    </span>

                    <span className="expense-report-field-value">
                      {Number(item.amount_ttc).toFixed(2)} €
                    </span>
                  </div>

                  {item.vat_rate != null && (
                    <div className="expense-report-field">
                      <span className="expense-report-field-label">
                        {translations.itemFields.vatRate}
                      </span>

                      <span className="expense-report-field-value">
                        {Number(item.vat_rate).toFixed(2)} %
                      </span>
                    </div>
                  )}

                  {item.vat_amount != null && (
                    <div className="expense-report-field">
                      <span className="expense-report-field-label">
                        {translations.itemFields.vatAmount}
                      </span>

                      <span className="expense-report-field-value">
                        {Number(item.vat_amount).toFixed(2)} €
                      </span>
                    </div>
                  )}

                  {item.amount_ht != null && (
                    <div className="expense-report-field">
                      <span className="expense-report-field-label">
                        {translations.itemFields.amountHt}
                      </span>

                      <span className="expense-report-field-value">
                        {Number(item.amount_ht).toFixed(2)} €
                      </span>
                    </div>
                  )}

                  <div className="expense-report-field">
                    <span className="expense-report-field-label">
                      {translations.itemFields
                        .vatRecoverable}
                    </span>

                    <span className="expense-report-field-value">
                      {item.is_vat_recoverable
                        ? t.common.yes
                        : t.common.no}
                    </span>
                  </div>

                  {item.comment && (
                    <div className="expense-report-field expense-report-field-full">
                      <span className="expense-report-field-label">
                        {translations.fields.comment}
                      </span>

                      <span className="expense-report-field-value">
                        {item.comment}
                      </span>
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="expense-panel right-panel">
        <div className="expense-report-items-header">
          <h2 className="expense-report-section-title">
            {translations.sections.attachments}
          </h2>
        </div>

        {attachments.length === 0 ? (
          <div className="expense-report-empty">
            {translations.empty.attachments}
          </div>
        ) : (
          <div className="expense-report-items-list">
            {attachments.map((attachment) => (
              <article
                key={attachment.uuid}
                className="expense-report-item-card attachment-card"
              >
                <div className="expense-report-header">
                  <div>
                    <h3 className="expense-report-item-title">
                      {attachment.file_name}
                    </h3>

                    <span className="expense-item-type-badge">
                      {formatFileSize(
                        attachment.file_size
                      )}
                    </span>
                  </div>

                  <div className="header-actions">
                    <button
                      type="button"
                      className="expense-action-btn edit-category-submit"
                      onClick={() =>
                        handleViewAttachment(attachment.uuid)
                      }
                    >
                      {translations.actions.openFull}
                    </button>

                    <button
                      type="button"
                      className="expense-action-btn category-download-btn" 
                      onClick={() => {
                        handleDownloadAttachment(attachment.uuid, attachment.file_name);
                      }}
                    >
                      {translations.actions.download}
                    </button>
                  </div>
                </div>

                <hr className="expense-report-divider" />

                <div className="attachment-preview-container">
                  {previewUrls[attachment.uuid] ? (
                    <img
                      src={previewUrls[attachment.uuid]}
                      alt={
                        translations.attachments.receiptAlt
                      }
                      className="attachment-preview-image"
                    />
                  ) : (
                    <div className="attachment-loading">
                      {
                        translations.attachments
                          .loadingPreview
                      }
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {/* Modale de confirmation de suppression (Note entière) */}
      <ConfirmDialog
        isOpen={isDeleteExpenseDialogOpen}
        title={t.common?.deleteConfirmTitle || "Confirmer la suppression"}
        content={translations.confirm.deleteReport}
        cancelText={t.common?.cancel || "Annuler"}
        confirmText={t.common?.delete || "Supprimer"}
        onCancel={() => setIsDeleteExpenseDialogOpen(false)}
        onConfirm={executeDeleteExpense}
        variant="danger"
      />

      {/* Modale de confirmation de suppression (Ligne de frais / Item) */}
      <ConfirmDialog
        isOpen={isDeleteItemDialogOpen}
        title={t.common?.deleteConfirmTitle || "Confirmer la suppression"}
        content={translations.confirm.deleteItem}
        cancelText={t.common?.cancel || "Annuler"}
        confirmText={t.common?.delete || "Supprimer"}
        onCancel={() => {
          setIsDeleteItemDialogOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={executeDeleteItem}
        variant="danger"
      />

      {/* Modale de confirmation de modification de statut */}
      <ConfirmDialog
        isOpen={isUpdateStatusDialogOpen}
        title={t.common?.saveConfirmTitle || "Confirmer l'action"}
        content={statusToUpdate ? replaceVariable(translations.confirm.changeStatus, "status", translateStatus(statusToUpdate)) : ""}
        cancelText={t.common?.cancel || "Annuler"}
        confirmText={t.common?.confirm || "Confirmer"}
        onCancel={() => {
          setIsUpdateStatusDialogOpen(false);
          setStatusToUpdate(null);
        }}
        onConfirm={executeUpdateStatus}
        variant="primary" 
      />
    </div>
  );
}

export default ExpenseReportDetailsCard;